import { Ui } from "@smartfaster-ui/components";
import "@smartfaster-ui/components/styles.css";
import {
  huubLightTheme,
  huubLightThemeConfig,
} from "@smartfaster-ui/themes/huub";
import "@smartfaster-ui/themes/styles.css";
import "@smartfaster-ui/theming/styles.css";
import "./smartfaster-overrides.css";
import { useEffect } from "react";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import type { PageOutletContext } from "../../hooks/usePageReady";
import { usePageReady } from "../../hooks/usePageReady";
import { useCompactLayout } from "../../hooks";
import { Canvas } from "./Canvas";
import { CompactControls } from "./CompactControls";
import {
  bottomControlsStyle,
  canvasWrapperStyle,
  errorOverlayStyle,
  overlayControlsStyle,
  screenRootStyle,
} from "./InteriorDesignerScreen.css";
import {
  InteriorDesignerProvider,
  useInteriorDesigner,
} from "./context/InteriorDesignerContext";
import { ErrorBanner } from "./ErrorBanner";
import { useConnectivity } from "./hooks/useConnectivity";
import { useGeneration } from "./hooks/useGeneration";
import { useHistoryActions } from "./hooks/useHistoryActions";
import { usePhotoLoader } from "./hooks/usePhotoLoader";
import { usePhotoNavigation } from "./hooks/usePhotoNavigation";
import { useVocab } from "./hooks/useVocab";
import { ActivePanel } from "./panels/ActivePanel";
import { WayfindingControls } from "./WayfindingControls";

const HUUB_LIGHT_THEME = {
  className: huubLightTheme,
  config: huubLightThemeConfig,
} as const;

type InteriorDesignerScreenInnerProps = {
  /**
   * Backend photo ID from URL params
   */
  photoId: string;
};

function InteriorDesignerScreenInner({
  photoId,
}: InteriorDesignerScreenInnerProps) {
  const { state, dispatch } = useInteriorDesigner();
  const { setLayoutConfig } = useOutletContext<PageOutletContext>();
  const { navigateToNode } = useHistoryActions();
  const { vocab } = useVocab();
  const { isCompact } = useCompactLayout();

  usePageReady();

  usePhotoLoader({ photoId, dispatch });

  useGeneration({
    generation: state.generation,
    dispatch,
    vocabVersion: vocab?.version ?? null,
  });

  useConnectivity({
    dispatch,
    photoId,
    activeNodeId: state.activeNodeId,
    navigateToNode,
  });

  usePhotoNavigation({
    isNavigating: state.isNavigating,
    activeNodeId: state.activeNodeId,
    dispatch,
  });

  const handleRetry = () => dispatch({ type: "GENERATION_RETRIED" });
  const handleDismiss = () => dispatch({ type: "GENERATION_DISMISSED" });

  useEffect(() => {
    // On compact the layout header stays visible so its mobile menu button
    // floats over the full-bleed canvas; the AI agent button is hidden. On
    // desktop the screen is full-bleed with the controls overlaid.
    setLayoutConfig({
      header: isCompact,
      sideAgent: false,
      headerActions: false,
      // Full-bleed only on compact; on desktop the bordered overlay frame stays.
      fullBleed: isCompact,
    });
    return () =>
      setLayoutConfig({
        header: true,
        sideAgent: true,
        headerActions: true,
        fullBleed: false,
      });
  }, [setLayoutConfig, isCompact]);

  return (
    <div className={screenRootStyle} data-testid="interior-designer-screen">
      <div className={canvasWrapperStyle}>
        <Canvas pan={isCompact} />

        {!isCompact && (
          <div className={overlayControlsStyle}>
            <WayfindingControls overlay />
          </div>
        )}
      </div>

      {isCompact && (
        <div className={bottomControlsStyle}>
          <CompactControls />
        </div>
      )}

      <ActivePanel />

      {state.generation.status === "error" && (
        <div className={errorOverlayStyle}>
          <ErrorBanner
            errorCode={state.generation.errorCode}
            onRetry={handleRetry}
            onDismiss={handleDismiss}
          />
        </div>
      )}
    </div>
  );
}

export function InteriorDesignerScreen() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const photoId = searchParams.get("photoId");

  usePageReady({ loading: true });

  useEffect(() => {
    if (!photoId) {
      navigate("/", { replace: true });
    }
  }, [photoId, navigate]);

  if (!photoId) {
    return null;
  }

  return (
    <Ui theme={HUUB_LIGHT_THEME} toastPosition="top-center">
      <InteriorDesignerProvider key={photoId} photoId={photoId}>
        <InteriorDesignerScreenInner photoId={photoId} />
      </InteriorDesignerProvider>
    </Ui>
  );
}
